/**
 * Wallet — the client's credits, membership, purchases, redemptions and a full
 * transaction history. Designed to grow: the Membership block holds the (seeded
 * but-off) subscription plans, and Buy Credits holds the one-time packs; both
 * route through Stripe Checkout once STRIPE_SECRET_KEY is set (503 → friendly
 * "not available yet" until then).
 *
 *   GET  /api/billing/me            balance + active subscription
 *   GET  /api/billing/packs         credit packs (catalog)
 *   GET  /api/billing/plans         subscription plans (catalog)
 *   GET  /api/billing/transactions  the ledger (history)
 *   POST /api/coupons/redeem        redeem a code → credits
 *   POST /api/billing/checkout      Stripe Checkout (pack_id | plan_id)
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { ArrowLeft, Coins, Ticket, Crown, Plus, ArrowDownLeft, ArrowUpRight } from 'lucide-react';
import { api, ApiError } from '@/lib/api';

interface BillingMe {
  balance: number;
  subscription: {
    id: string; status: string; current_period_end: string | null;
    billing_interval: string | null; price_cents: number | null; plan_name: string | null;
  } | null;
}
interface Pack { id: string; name: string; description: string | null; credits: number; price_cents: number; }
interface Plan { id: string; name: string; description: string | null; price_cents: number; billing_interval: string; }
interface Txn {
  id: string; type: string; credits: number; amount_cents: number;
  status: string; description: string | null; created_at: string; pack_name: string | null;
}

const fmtCents = (c: number) => `$${(c / 100).toFixed(c % 100 === 0 ? 0 : 2)}`;
const fmtDate = (iso: string) => {
  const d = new Date(iso.includes('T') || iso.includes('Z') ? iso : iso.replace(' ', 'T') + 'Z');
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
};

export default function Credits() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [me, setMe] = useState<BillingMe | null>(null);
  const [packs, setPacks] = useState<Pack[]>([]);
  const [plans, setPlans] = useState<Plan[]>([]);
  const [txns, setTxns] = useState<Txn[]>([]);
  const [loading, setLoading] = useState(true);
  const [code, setCode] = useState('');
  const [redeeming, setRedeeming] = useState(false);
  const [message, setMessage] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [checkoutBanner, setCheckoutBanner] = useState<{ kind: 'ok' | 'error'; text: string } | null>(null);
  const [buying, setBuying] = useState<string | null>(null);

  const load = useCallback(async () => {
    const [meRes, packRes, planRes, txnRes] = await Promise.allSettled([
      api.get<BillingMe>('/api/billing/me'),
      api.get<{ packs: Pack[] }>('/api/billing/packs'),
      api.get<{ plans: Plan[] }>('/api/billing/plans'),
      api.get<{ transactions: Txn[] }>('/api/billing/transactions'),
    ]);
    if (meRes.status === 'fulfilled') setMe(meRes.value);
    if (packRes.status === 'fulfilled') setPacks(packRes.value.packs ?? []);
    if (planRes.status === 'fulfilled') setPlans(planRes.value.plans ?? []);
    if (txnRes.status === 'fulfilled') setTxns(txnRes.value.transactions ?? []);
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  // Returning from Stripe Checkout: ?status=success|cancelled. Show a banner and
  // strip the param so a refresh doesn't re-show it. On success, reload to pick
  // up the new balance once the webhook has credited the ledger.
  useEffect(() => {
    const status = searchParams.get('status');
    if (status !== 'success' && status !== 'cancelled') return;
    setCheckoutBanner(
      status === 'success'
        ? { kind: 'ok', text: 'Payment received — your credits are on the way.' }
        : { kind: 'error', text: 'Checkout cancelled. Nothing was charged.' }
    );
    if (status === 'success') load();
    searchParams.delete('status');
    setSearchParams(searchParams, { replace: true });
  }, [searchParams, setSearchParams, load]);

  async function redeem() {
    const c = code.trim();
    if (!c) return;
    setRedeeming(true);
    setMessage(null);
    try {
      const r = await api.post<{ credits: number; balance: number }>('/api/coupons/redeem', { code: c });
      setCode('');
      setMessage({ kind: 'ok', text: `Added ${r.credits} credits!` });
      await load();
    } catch (e) {
      setMessage({ kind: 'error', text: friendly(e instanceof ApiError ? e.code : 'error') });
    } finally {
      setRedeeming(false);
    }
  }

  async function checkout(body: { pack_id?: string; plan_id?: string }, key: string) {
    setBuying(key);
    setMessage(null);
    try {
      const r = await api.post<{ url: string }>('/api/billing/checkout', body);
      if (r.url) window.location.href = r.url;
    } catch (e) {
      const code = e instanceof ApiError ? e.code : 'error';
      setMessage({
        kind: 'error',
        text: code === 'stripe_not_configured' ? "Purchases aren't available yet — check back soon." : 'Could not start checkout.',
      });
    } finally {
      setBuying(null);
    }
  }

  const sub = me?.subscription;

  return (
    <div className="px-5 pt-[max(16px,env(safe-area-inset-top))] pb-4 flex flex-col gap-4">
      <div className="flex items-center gap-3">
        <button onClick={() => navigate(-1)} className="p-1" style={{ color: 'var(--champagne)' }} aria-label="Back">
          <ArrowLeft size={22} />
        </button>
        <h1 className="display" style={{ fontSize: '1.9rem' }}>Wallet</h1>
      </div>

      {/* Post-checkout banner (returning from Stripe) */}
      {checkoutBanner && (
        <div
          className="card text-sm"
          style={{
            borderColor: checkoutBanner.kind === 'ok' ? 'var(--success)' : 'var(--error)',
            color: checkoutBanner.kind === 'ok' ? 'var(--success)' : 'var(--error)',
            background:
              checkoutBanner.kind === 'ok' ? 'rgba(34,197,94,0.10)' : 'rgba(239,68,68,0.10)',
          }}
          role="status"
        >
          {checkoutBanner.text}
        </div>
      )}

      {/* Balance hero */}
      <div className="card" style={{ background: 'linear-gradient(135deg, rgba(201,168,106,0.18), var(--surface-elevated))' }}>
        <div className="flex items-center gap-2 mb-1">
          <Coins size={14} style={{ color: 'var(--champagne)' }} />
          <span className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--gold-muted)' }}>Credit Balance</span>
        </div>
        <p style={{ fontFamily: 'var(--font-display), Georgia, serif', fontSize: '3rem', lineHeight: 1.0, color: 'var(--cream)' }}>
          {loading ? '—' : me?.balance ?? 0}
        </p>
        <p className="text-xs" style={{ color: 'var(--cream-muted)' }}>credits available</p>
      </div>

      {/* Membership */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <Crown size={16} style={{ color: 'var(--champagne)' }} />
          <p className="text-sm font-semibold" style={{ color: 'var(--cream)' }}>Membership</p>
        </div>
        {sub ? (
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-semibold text-white">{sub.plan_name ?? 'Active plan'}</p>
              <p className="text-xs" style={{ color: 'var(--gray-400)' }}>
                {sub.price_cents != null ? `${fmtCents(sub.price_cents)}/${sub.billing_interval ?? 'mo'}` : ''}
                {sub.current_period_end ? ` · renews ${fmtDate(sub.current_period_end)}` : ''}
              </p>
            </div>
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ background: 'rgba(34,197,94,0.12)', color: 'var(--success)' }}>
              {sub.status}
            </span>
          </div>
        ) : plans.length > 0 ? (
          <div className="flex flex-col gap-2">
            <p className="text-xs" style={{ color: 'var(--gray-400)' }}>No active plan. Choose a membership:</p>
            {plans.map((p) => (
              <button
                key={p.id}
                onClick={() => checkout({ plan_id: p.id }, p.id)}
                disabled={buying === p.id}
                className="flex items-center justify-between text-left rounded-md px-3 py-2.5"
                style={{ background: 'var(--surface)', border: '1px solid rgba(244,239,227,0.10)' }}
              >
                <div>
                  <p className="text-sm font-medium" style={{ color: 'var(--cream)' }}>{p.name}</p>
                  {p.description && <p className="text-xs" style={{ color: 'var(--gray-500)' }}>{p.description}</p>}
                </div>
                <span className="text-sm font-semibold" style={{ color: 'var(--champagne)' }}>
                  {buying === p.id ? '…' : `${fmtCents(p.price_cents)}/${p.billing_interval === 'year' ? 'yr' : 'mo'}`}
                </span>
              </button>
            ))}
          </div>
        ) : (
          <p className="text-xs" style={{ color: 'var(--gray-500)' }}>Membership plans are coming soon.</p>
        )}
      </div>

      {/* Buy credits */}
      {packs.length > 0 && (
        <div className="card">
          <div className="flex items-center gap-2 mb-3">
            <Plus size={16} style={{ color: 'var(--champagne)' }} />
            <p className="text-sm font-semibold" style={{ color: 'var(--cream)' }}>Buy credits</p>
          </div>
          <div className="grid grid-cols-2 gap-2">
            {packs.map((p) => (
              <button
                key={p.id}
                onClick={() => checkout({ pack_id: p.id }, p.id)}
                disabled={buying === p.id}
                className="text-left rounded-md p-3"
                style={{ background: 'var(--surface)', border: '1px solid rgba(244,239,227,0.10)' }}
              >
                <p className="text-sm font-semibold" style={{ color: 'var(--cream)' }}>{p.name}</p>
                <p className="text-xs" style={{ color: 'var(--gray-400)' }}>{p.credits} credits</p>
                <p className="text-sm font-semibold mt-1" style={{ color: 'var(--champagne)' }}>
                  {buying === p.id ? '…' : fmtCents(p.price_cents)}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Redeem a code */}
      <div className="card">
        <div className="flex items-center gap-2 mb-3">
          <Ticket size={16} style={{ color: 'var(--champagne)' }} />
          <p className="text-sm font-semibold" style={{ color: 'var(--cream)' }}>Redeem a code</p>
        </div>
        <div className="flex gap-2">
          <input
            className="input flex-1"
            placeholder="Enter coupon code"
            value={code}
            onChange={(e) => setCode(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') redeem(); }}
            autoCapitalize="characters"
            aria-label="Coupon code"
          />
          <button className="btn-gold px-5" onClick={redeem} disabled={redeeming || !code.trim()}>
            {redeeming ? '…' : 'Redeem'}
          </button>
        </div>
        {message && (
          <p className="text-sm mt-3" style={{ color: message.kind === 'ok' ? 'var(--success)' : 'var(--error)' }}>
            {message.text}
          </p>
        )}
      </div>

      {/* History */}
      <div className="card">
        <p className="text-xs font-medium uppercase tracking-wider mb-3" style={{ color: 'var(--gold-muted)' }}>History</p>
        {loading ? (
          <p className="text-xs" style={{ color: 'var(--gray-500)' }}>Loading…</p>
        ) : txns.length === 0 ? (
          <p className="text-xs" style={{ color: 'var(--gray-500)' }}>No activity yet. Redeem a code or buy credits to get started.</p>
        ) : (
          <div className="flex flex-col">
            {txns.map((t) => {
              const positive = t.credits >= 0;
              return (
                <div key={t.id} className="flex items-center gap-3 py-2.5 border-t first:border-t-0" style={{ borderColor: 'rgba(255,255,255,0.06)' }}>
                  <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                       style={{ background: positive ? 'rgba(34,197,94,0.12)' : 'rgba(239,68,68,0.12)' }}>
                    {positive ? <ArrowDownLeft size={14} style={{ color: 'var(--success)' }} /> : <ArrowUpRight size={14} style={{ color: 'var(--error)' }} />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-white truncate">{label(t)}</p>
                    <p className="text-xs" style={{ color: 'var(--gray-500)' }}>{fmtDate(t.created_at)}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-semibold" style={{ color: positive ? 'var(--success)' : 'var(--error)' }}>
                      {positive ? '+' : ''}{t.credits} cr
                    </p>
                    {t.amount_cents !== 0 && (
                      <p className="text-xs" style={{ color: 'var(--gray-500)' }}>{fmtCents(Math.abs(t.amount_cents))}</p>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

function label(t: Txn): string {
  if (t.description) return t.description;
  switch (t.type) {
    case 'purchase': return t.pack_name ? `Purchased ${t.pack_name}` : 'Credit purchase';
    case 'grant': return 'Credits granted';
    case 'spend': return 'Credits used';
    case 'refund': return 'Refund';
    default: return t.type;
  }
}

function friendly(code: string): string {
  switch (code) {
    case 'invalid_code': return "That code isn't valid.";
    case 'coupon_inactive': return 'That code is no longer active.';
    case 'coupon_expired': return 'That code has expired.';
    case 'already_redeemed': return "You've already redeemed that code.";
    case 'coupon_exhausted': return 'That code has reached its usage limit.';
    case 'code_required': return 'Enter a code to redeem.';
    default: return 'Could not redeem that code. Please try again.';
  }
}
